import HTTP from 'Utils/HTTP';

export default class Validator {
	static validate(input, options = { ignoreDirty: false, form: null }) {
		const isRequired = input.hasAttribute('required');
		const isDirty = input.dataset.dirty || input.dataset.alwaysDirty;

		let rootElement = document;
		if (options.form) {
			rootElement = options.form;
		}

		const validateAs = input.dataset.validateAs ?? 'text';
		const validateService = input.dataset.validateService;
		const validateCustom = input.dataset.validateCustom;

		const validateItems = validateAs.split('|');
		let serviceResponse;
		if (validateService === 'iban') {
			const iban = input.value;
			serviceResponse = HTTP.post('/public-api/iban', {
				iban,
			});
		}

		return validateItems.reduce(
			(prev, curr) => {
				curr = curr.charAt(0).toUpperCase() + curr.slice(1);

				if (!isRequired && input.dataset.validateAs == null) {
					curr = null;
				}

				if (!curr)
					return {
						error: false,
						isValid: true,
						isDirty,
					};

				const validateFieldCondition = input.dataset.validateFieldCondition;
				if (!Validator.validateFieldConditionValid(validateFieldCondition))
					return {
						error: false,
						isValid: true,
						isDirty,
					};

				if (typeof Validator[`validateAs${curr}`] === 'undefined') {
					console.error('Unknown validate type:', curr);
					return {
						error: false,
						isValid: true,
						isDirty,
					};
				}

				const isValid = Validator[`validateAs${curr}`](input, { root: rootElement });
				prev.isDirty = isDirty;

				if (isDirty || options.ignoreDirty) {
					prev.error = !prev.error ? !isValid : true;
				}

				prev.isValid = prev.isValid ? isValid : false;
				if (validateService) {
					prev.service = serviceResponse;
				}

				if (validateCustom) {
					prev.custom = validateCustom;
				}

				return prev;
			},
			{
				error: false,
				isValid: true,
				isDirty: false,
			}
		);
	}

	static validateFieldConditionValid(condition) {
		if (condition == null) return true;
		const conditionSplit = condition.split('|');
		if (conditionSplit.length !== 2) {
			console.error('Malformed validate condition:', condition);
			return true;
		}

		const [conditionFieldSelector, conditionValue] = conditionSplit;
		const field = document.querySelector(conditionFieldSelector);
		if (!field) return false;
		switch (field.type.toLowerCase()) {
			case 'text': {
				return String(field.value) === conditionValue;
			}

			case 'radio':
			case 'checkbox': {
				return String(field.checked).toLowerCase() === conditionValue.toLowerCase();
			}

			default: {
				console.error('Unknow validate field type', field, field.type);
			}
		}
		return false;
	}

	static isItOptional(input) {
		return !input.hasAttribute('required') || input.hasAttribute('required') == null;
	}

	static validateAsText(input) {
		const value = input.value.trim();
		const isOptional = this.isItOptional(input);
		return value !== '' && value != null && !isOptional;
	}

	static validateAsStreetNr(input) {
		const value = input.value.trim();
		const expression = /^\S+/i;
		return value !== '' && value != null && expression.test(value);
	}

	static validateAsPlz(input) {
		const value = input.value.trim();
		const expression = /^[0-9]{5}$/i;
		return expression.test(value);
	}

	static validateAsTelephone(input) {
		const value = input.value.trim();
		const isOptional = this.isItOptional(input);
		const expression = /^([0-9()\\/]| |\+)+$/i;
		return expression.test(value) || (isOptional && value === '');
	}

	static validateAsEmail(input) {
		const value = input.value.trim();
		if (value === '' && this.isItOptional(input)) return true;
		const expression =
			/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;
		return expression.test(value);
	}

	static validateAsBusinessEmail(input) {
		const value = input.value.trim();
		if (value === '' && this.isItOptional(input)) return true;
		const expression =
			/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i;
		if (!expression.test(value)) return false;

		const blockedDomains = /@(gmail|web|gmx|outlook|1und1|t-online|yahoo|icloud|hotmail)\./;
		return !blockedDomains.test(value);
	}

	static validateAsIban(input) {
		const value = input.value.trim().replace(/\s/giu, '');
		const expression =
			/^AL\d{10}[0-9A-Z]{16}$|^AD\d{10}[0-9A-Z]{12}$|^AT\d{18}$|^BH\d{2}[A-Z]{4}[0-9A-Z]{14}$|^BE\d{14}$|^BA\d{18}$|^BG\d{2}[A-Z]{4}\d{6}[0-9A-Z]{8}$|^HR\d{19}$|^CY\d{10}[0-9A-Z]{16}$|^CZ\d{22}$|^DK\d{16}$|^FO\d{16}$|^GL\d{16}$|^DO\d{2}[0-9A-Z]{4}\d{20}$|^EE\d{18}$|^FI\d{16}$|^FR\d{12}[0-9A-Z]{11}\d{2}$|^GE\d{2}[A-Z]{2}\d{16}$|^DE\d{20}$|^GI\d{2}[A-Z]{4}[0-9A-Z]{15}$|^GR\d{9}[0-9A-Z]{16}$|^HU\d{26}$|^IS\d{24}$|^IE\d{2}[A-Z]{4}\d{14}$|^IL\d{21}$|^IT\d{2}[A-Z]\d{10}[0-9A-Z]{12}$|^KW\d{2}[A-Z]{4}22!$|^LV\d{2}[A-Z]{4}[0-9A-Z]{13}$|^LB\d{6}[0-9A-Z]{20}$|^LI\d{7}[0-9A-Z]{12}$|^LT\d{18}$|^LU\d{5}[0-9A-Z]{13}$|^MK\d{5}[0-9A-Z]{10}\d{2}$|^MT\d{2}[A-Z]{4}\d{5}[0-9A-Z]{18}$|^MR13\d{23}$|^MU\d{2}[A-Z]{4}\d{19}[A-Z]{3}$|^MC\d{12}[0-9A-Z]{11}\d{2}$|^ME\d{20}$|^NL\d{2}[A-Z]{4}\d{10}$|^NO\d{13}$|^PT\d{23}$|^RO\d{2}[A-Z]{4}[0-9A-Z]{16}$|^SM\d{2}[A-Z]\d{10}[0-9A-Z]{12}$|^SA\d{4}[0-9A-Z]{18}$|^RS\d{20}$|^SK\d{22}$|^SI\d{17}$|^ES\d{22}$|^SE\d{22}$|^CH\d{7}[0-9A-Z]{12}$|^TN59\d{20}$|^TR\d{7}[0-9A-Z]{17}$|^AE\d{21}$|^GB\d{2}[A-Z]{4}\d{14}$|^PL\d{10}[0-9A-Z]{16}n$/i;
		return expression.test(value);
	}

	static validateAsBic(input) {
		const value = input.value.trim().replace(/\s/giu, '');
		const expression = /^[A-Z]{6,6}[A-Z2-9][A-NP-Z0-9]([A-Z0-9]{3,3}){0,1}$/i;
		return expression.test(value);
	}

	static validateAsPassword(input) {
		const value = input.value.trim();
		if (input.getAttribute('required') == null && value.length === 0) return true;
		const minLength = input.dataset.validateData || 6;
		return value !== '' && value != null && value.length >= minLength;
	}

	static validateAsDate(input) {
		const value = input.value.trim();
		if (!input.required && value === '') return true;
		return value !== '' && value != null;
	}

	static validateAsSelect(input) {
		const value = input.options[input.selectedIndex].value;
		return value !== '' && value != null && value !== '-1';
	}

	static validateAsCheckbox(input) {
		return input.checked;
	}

	static validateAsCheckboxGroup(input, options) {
		if (!input.hasAttribute('required')) return true;
		const checkboxes = Array.from(options.root.querySelectorAll(`[name="${input.name}"]`));
		let oneChecked = false;
		checkboxes.forEach(checkbox => {
			if (checkbox.checked) oneChecked = true;
		});
		return oneChecked;
	}

	static validateAsCompareWithInput(input) {
		const value = input.value.trim();
		const compareWithSelector = input.dataset.compareWith;

		if (compareWithSelector == null) return false;

		const compareWithInput = document.querySelector(compareWithSelector);

		if (compareWithInput == null) return false;

		const compareWithValue = compareWithInput.value;

		return value === compareWithValue;
	}

	static validateAsRadio(input) {
		const { name } = input;
		const formTarget = input.dataset.validateFormTarget || 'body';

		return document.querySelectorAll(`${formTarget} [name="${name}"]:checked`).length > 0;
	}

	static validateAsRegExp(input) {
		const value = input.value.trim();
		const expression = new RegExp(input.dataset.validateData);
		return expression.test(value);
	}

	static validateAsNumberRange(input) {
		const value = parseInt(input.value.trim());
		const range = input.dataset.validateData.split('-');
		const min = parseInt(range[0]);
		const max = parseInt(range[1]);
		return value >= min && value <= max;
	}

	static addLeadingZero(value) {
		return parseInt(value) < 10 ? `0${value}` : value;
	}
}
